import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Alert, CircularProgress } from '@mui/material';
import { useLicense } from '../context/LicenseContext';

const LICENSE_KEY_PATTERN = /^(EVAL|PROD)-[0-9a-fA-F]{16}$/;

export default function LicenseActivation() {
  const { activateLicense, isLoading } = useLicense();
  const [error, setError] = useState('');
  const [licenseKey, setLicenseKey] = useState('');

  const handleManualActivation = async () => {
    try {
      setError('');
      
      // Validate license key format
      if (!licenseKey.trim()) {
        setError('Please enter a license key');
        return;
      }

      if (!LICENSE_KEY_PATTERN.test(licenseKey.trim())) {
        setError('Invalid license key format. Expected: EVAL-xxxxxxxxxxxxxxxx or PROD-xxxxxxxxxxxxxxxx');
        return;
      }

      // Call API to validate and activate license
      const result = await activateLicense(licenseKey.trim());
      
      if (result.success) {
        // Successful activation will trigger a page reload
        window.location.reload();
      } else {
        setError(result.error || 'Failed to activate license');
      }
    } catch (err) {
      console.error('Activation error:', err);
      setError(err.message || 'An error occurred during license activation');
    }
  };

  return (
    <Box sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        License Activation
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Typography variant="body2" sx={{ mb: 2 }}>
        Enter your license key to activate access to developer features.
      </Typography>
      
      <TextField
        fullWidth
        label="License Key"
        value={licenseKey}
        onChange={(e) => setLicenseKey(e.target.value)}
        placeholder="e.g., EVAL-993a01c63cba477f"
        disabled={isLoading}
        sx={{ mb: 2 }}
      />

      <Button 
        variant="contained" 
        onClick={handleManualActivation}
        fullWidth
        disabled={isLoading}
      >
        {isLoading ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          'Activate License'
        )}
      </Button>

      <Typography variant="caption" sx={{ mt: 2, display: 'block', textAlign: 'center', color: 'text.secondary' }}>
        License keys follow the format: EVAL-xxxxxxxxxxxxxxxx for evaluation licenses
        or PROD-xxxxxxxxxxxxxxxx for production licenses
      </Typography>
    </Box>
  );
} 